import { Search, FileText, Scale, Siren, Activity, BellRing, GraduationCap, Mail, Phone } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const SERVIZI = [
  {
    icon: Search,
    titolo: "Sopralluoghi e analisi documentale",
    desc: "Sopralluogo periodico in azienda ed esame della documentazione aziendale in materia di igiene e sicurezza del lavoro.",
  },
  {
    icon: FileText,
    titolo: "Documento di Valutazione dei Rischi (DVR)",
    desc: "Redazione e aggiornamento del DVR, lo strumento centrale per identificare, analizzare e gestire i rischi presenti in azienda.",
  },
  {
    icon: Scale,
    titolo: "Rapporti con gli organi di controllo",
    desc: "Assistenza in tutti i rapporti con gli organi di vigilanza e controllo in materia di sicurezza e tutela dei lavoratori.",
  },
  {
    icon: Siren,
    titolo: "Piano di emergenza",
    desc: "Elaborazione del piano di emergenza aziendale: prevenzione incendi, evacuazione e gestione delle situazioni critiche.",
  },
  {
    icon: Activity,
    titolo: "Stress lavoro correlato",
    desc: "Valutazione dello stress lavoro correlato secondo le metodologie INAIL e le linee guida ministeriali.",
  },
  {
    icon: BellRing,
    titolo: "Segnalazione aggiornamenti normativi",
    desc: "Servizio continuo di assistenza con segnalazioni tempestive sugli aggiornamenti del D.Lgs. 81/08 e della normativa collegata.",
  },
  {
    icon: GraduationCap,
    titolo: "Formazione obbligatoria in materia di Sicurezza sul Lavoro",
    desc: "Percorsi formativi obbligatori rivolti a datori di lavoro, dirigenti, preposti, lavoratori, RSPP e addetti alle emergenze, in conformità al D.Lgs. 81/08 e s.m.i. e agli Accordi Stato-Regioni, finalizzati a garantire la corretta acquisizione delle competenze in materia di salute e sicurezza sui luoghi di lavoro.",
  },
];

const TELEFONI = ["06.68437.606", "06.68437.530"];

const FAQ = [
  { domanda: "La Sicurezza sul lavoro è obbligatoria per tutte le aziende?", risposta: "Sì, il D.Lgs. 81/08 e s.m.i. impone a ogni datore di lavoro specifici obblighi in materia di salute e sicurezza, indipendentemente dal numero di dipendenti. Ti aiutiamo a individuare gli adempimenti specifici per la tua attività." },
  { domanda: "Che cos'è il DVR?", risposta: "Il Documento di Valutazione dei Rischi è lo strumento con cui il datore di lavoro individua, analizza e gestisce i rischi presenti in azienda, definendo le misure di prevenzione e protezione necessarie. Va aggiornato in occasione di modifiche significative dell'organizzazione, di nuovi rischi rilevati o di variazioni normative: ci occupiamo noi del monitoraggio e dell'aggiornamento." },
  { domanda: "La formazione va ripetuta nel tempo?", risposta: "Sì, gran parte della formazione obbligatoria prevede corsi di aggiornamento periodico secondo le tempistiche stabilite dagli Accordi Stato-Regioni. Ti segnaliamo per tempo le scadenze formative." },
  { domanda: "Cosa succede in caso di controllo ispettivo?", risposta: "Il nostro servizio prevede assistenza diretta durante i rapporti con gli organi di vigilanza, per supportarti nella gestione della verifica e nella corretta esibizione della documentazione richiesta." },
  { domanda: "Da dove si comincia se in azienda non è mai stato fatto nulla sulla sicurezza?", risposta: "Da un check up tecnico, per capire subito cosa serve e programmare le attività." },
  { domanda: "Cosa succede se non si rispettano gli obblighi in materia di sicurezza?", risposta: "Le violazioni possono comportare sanzioni amministrative e, nei casi più gravi, penali a carico del datore di lavoro e delle altre figure coinvolte nel sistema di prevenzione. Un'assistenza continuativa riduce sensibilmente il rischio di trovarsi in questa condizione, grazie al monitoraggio costante degli adempimenti." },
  { domanda: "È possibile occuparsene senza distogliere troppo tempo dal lavoro quotidiano?", risposta: "Sì: lasci a noi la parte tecnica e documentale, e ti concentri sulla tua attività." },
];

export default function SicurezzaLavoroPage() {
  return (
    <main id="main-content" data-testid="sicurezza-lavoro-page">
      {/* Hero */}
      <section className="relative h-[300px] md:h-[360px] bg-[var(--confcom-blue)] overflow-hidden">
        <img
          src="/local-assets/sicurezza-lavoro-hero.webp"
          alt="Sicurezza sul lavoro"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2 block" data-testid="hero-categoria-label">
                Sicurezza
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
                Sicurezza sul lavoro
              </h1>
            </div>

            {/* Bollino Promo.Ter — servizio erogato da */}
            <div
              className="shrink-0 inline-flex flex-col items-center gap-1.5 bg-white rounded-2xl px-5 py-3 shadow-lg border border-white/10"
              data-testid="hero-promoter-badge"
              aria-label="Servizio erogato da Promo.Ter"
            >
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--confcom-text-secondary)] font-['Encode_Sans_Semi_Condensed']">
                Servizio a cura di
              </span>
              <img
                src="/logo-promoter.webp"
                alt="Promo.Ter — Formazione e servizi per il terziario"
                className="h-16 sm:h-20 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
                Promo.Ter offre un servizio completo di consulenza in materia di Salute e Sicurezza nei luoghi di lavoro, in piena conformità con il <strong className="text-[var(--confcom-text-primary)]">D.Lgs. 81/08</strong> e s.m.i. Attraverso sopralluoghi, verifica della documentazione aziendale e redazione/aggiornamento del Documento di Valutazione dei Rischi, supportiamo l'azienda in ogni fase: dagli adempimenti normativi alla pianificazione delle emergenze, dalla valutazione dello stress lavoro-correlato al rapporto con gli organi di vigilanza, fino alla formazione completa del personale. Garantiamo aggiornamenti costanti e assistenza in caso di controlli ispettivi.
              </p>
            </div>
            <div className="h-[340px] rounded-xl overflow-hidden" data-testid="sicurezza-lavoro-intro-image">
              <img
                src="/local-assets/sicurezza-lavoro-hero.webp"
                alt="Tecnico con casco e gilet ad alta visibilità durante un sopralluogo di sicurezza"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Banner */}
      <section className="py-10 bg-[var(--confcom-blue)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg md:text-xl text-white font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed">
            La Sicurezza sul lavoro non è solo un obbligo di legge: è la base su cui costruire un'azienda solida e affidabile.
          </p>
        </div>
      </section>

      {/* Cosa offriamo */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-3 block">I servizi dedicati ai nostri associati</span>
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] tracking-tight">
              Cosa offriamo
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVIZI.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.titolo}
                  className="bg-white p-6 rounded-xl border border-[var(--confcom-border)]"
                  data-testid={`servizio-${item.titolo.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="w-11 h-11 bg-[var(--confcom-bg-blue-light)] rounded-xl flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[var(--confcom-blue)]" aria-hidden="true" />
                  </div>
                  <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">
                    {item.titolo}
                  </h3>
                  <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] tracking-tight mb-10">
            Domande frequenti
          </h2>
          <div className="space-y-6 max-w-4xl">
            {FAQ.map((f) => (
              <div key={f.domanda} className="border-b border-[var(--confcom-border)] pb-6" data-testid={`faq-${f.domanda.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{f.domanda}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{f.risposta}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 59, 117, 0.95)" }} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="inline-block bg-[#56B7E6] text-[var(--confcom-blue)] text-xs font-bold uppercase tracking-wide px-4 py-1.5 rounded-full mb-5">
            Verifica la tua documentazione
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] mb-8 uppercase">Contattaci subito per un check-up gratuito</h2>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a
              href="mailto:commerciale.promoter@confcommercioroma.it"
              className="inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold hover:opacity-90 transition-opacity"
              data-testid="cta-email-sicurezza"
            >
              <Mail size={16} aria-hidden="true" /> commerciale.promoter@confcommercioroma.it
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-y-2 text-sm text-white/80">
            <Phone size={15} className="text-white/70 mr-3" aria-hidden="true" />
            {TELEFONI.map((tel, i) => (
              <span key={tel} className="flex items-center">
                <a
                  href={`tel:+39${tel.replace(/\./g, "")}`}
                  className="hover:text-white transition-colors"
                >
                  {tel}
                </a>
                {i < TELEFONI.length - 1 && (
                  <span className="w-1 h-1 rounded-full bg-white/40 mx-3" aria-hidden="true" />
                )}
              </span>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
